import { toSafeObjectOrNull as toObject, toSafeString } from '@shared/utils';
const CHECKLIST_UPDATE_TOOL_NAME = 'checklist_update';
const CHECKLIST_STEP_STATUS_PENDING = 'pending';
const CHECKLIST_STEP_STATUS_IN_PROGRESS = 'in_progress';
const CHECKLIST_STEP_STATUS_COMPLETED = 'completed';
const CHECKLIST_STEP_STATUS_SET = new Set([CHECKLIST_STEP_STATUS_PENDING, CHECKLIST_STEP_STATUS_IN_PROGRESS, CHECKLIST_STEP_STATUS_COMPLETED]);

const normalizeChecklistStepStatus = (value) => {
  const normalized = toSafeString(value).toLowerCase();

  if (!CHECKLIST_STEP_STATUS_SET.has(normalized)) {
    return null;
  }

  return normalized;
};

const normalizeActiveChecklist = (value) => {
  const source = toObject(value);

  if (!source) {
    return null;
  }

  const checklist = Array.isArray(source.checklist)
    ? source.checklist
        .map((step) => {
          const stepSource = toObject(step);

          if (!stepSource) {
            return null;
          }

          const normalizedStep = toSafeString(stepSource.step);
          const normalizedStatus = normalizeChecklistStepStatus(stepSource.status);

          if (!normalizedStep || !normalizedStatus) {
            return null;
          }

          return {
            step: normalizedStep,
            title: normalizedStep,
            status: normalizedStatus
          };
        })
        .filter(Boolean)
    : [];

  if (checklist.length < 1) {
    return null;
  }

  return {
    explanation: toSafeString(source.explanation) || null,
    updatedAt: toSafeString(source.updatedAt || source.updated_at) || null,
    checklist
  };
};

const areActiveChecklistsEqual = (leftChecklist, rightChecklist) => {
  return JSON.stringify(normalizeActiveChecklist(leftChecklist)) === JSON.stringify(normalizeActiveChecklist(rightChecklist));
};

const getToolNameFromEventPayload = (eventPayload) => {
  return toSafeString(eventPayload?.toolName || eventPayload?.payload?.toolName || eventPayload?.data?.toolName || eventPayload?.output?.toolName || eventPayload?.result?.toolName || eventPayload?.payload?.output?.toolName || eventPayload?.payload?.result?.toolName || eventPayload?.data?.output?.toolName || eventPayload?.data?.result?.toolName).toLowerCase();
};

const getChecklistUpdateLifecycleTypeFromEventPayload = (eventPayload = {}) => {
  return toSafeString(eventPayload?.type || eventPayload?.eventType || eventPayload?.payload?.type || eventPayload?.payload?.eventType).toLowerCase();
};

const CHECKLIST_UPDATE_LIFECYCLE_EVENT_TYPES = new Set(['tool_call', 'tool_result', 'tool.call', 'tool.result', 'line', 'codeblock', 'code_block']);
const CHECKLIST_UPDATE_RESULT_EVENT_TYPES = new Set(['tool_result', 'tool.result', 'codeblock', 'code_block']);

const isChecklistUpdateLifecycleEvent = (eventPayload = {}) => {
  const lifecycleType = getChecklistUpdateLifecycleTypeFromEventPayload(eventPayload);

  if (!CHECKLIST_UPDATE_LIFECYCLE_EVENT_TYPES.has(lifecycleType)) {
    return false;
  }

  return getToolNameFromEventPayload(eventPayload) === CHECKLIST_UPDATE_TOOL_NAME;
};

const extractActiveChecklistCarrierFromValue = (value) => {
  const source = toObject(value);

  if (!source) {
    return {
      found: false,
      activeChecklist: null
    };
  }

  const candidateObjects = [source, source.result, source.data?.result, source.output, source.output?.result, source.output?.data?.result, source.payload, source.payload?.result, source.payload?.data?.result];

  for (const candidate of candidateObjects) {
    const candidateObject = toObject(candidate);

    if (!candidateObject) {
      continue;
    }

    if (!Object.prototype.hasOwnProperty.call(candidateObject, 'activeChecklist')) {
      continue;
    }

    return {
      found: true,
      activeChecklist: normalizeActiveChecklist(candidateObject.activeChecklist)
    };
  }

  return {
    found: false,
    activeChecklist: null
  };
};

const extractChecklistUpdateResultActiveChecklist = (eventPayload = {}) => {
  if (!isChecklistUpdateLifecycleEvent(eventPayload)) {
    return {
      matched: false,
      hasActiveChecklist: false,
      activeChecklist: null
    };
  }

  const lifecycleType = getChecklistUpdateLifecycleTypeFromEventPayload(eventPayload);

  if (!CHECKLIST_UPDATE_RESULT_EVENT_TYPES.has(lifecycleType)) {
    return {
      matched: true,
      hasActiveChecklist: false,
      activeChecklist: null
    };
  }

  const resultCandidates = [eventPayload?.result, eventPayload?.payload?.result, eventPayload?.data?.result, eventPayload?.output, eventPayload?.payload?.output, eventPayload?.data?.output, eventPayload];

  for (const candidate of resultCandidates) {
    const extracted = extractActiveChecklistCarrierFromValue(candidate);

    if (extracted.found) {
      return {
        matched: true,
        hasActiveChecklist: true,
        activeChecklist: extracted.activeChecklist
      };
    }
  }

  return {
    matched: true,
    hasActiveChecklist: false,
    activeChecklist: null
  };
};

export { areActiveChecklistsEqual, CHECKLIST_STEP_STATUS_COMPLETED, CHECKLIST_STEP_STATUS_IN_PROGRESS, CHECKLIST_STEP_STATUS_PENDING, CHECKLIST_UPDATE_TOOL_NAME, extractChecklistUpdateResultActiveChecklist, getChecklistUpdateLifecycleTypeFromEventPayload, getToolNameFromEventPayload, isChecklistUpdateLifecycleEvent, normalizeActiveChecklist, normalizeChecklistStepStatus };
